import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/layout/Layout';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  Plus, Eye, Edit, Trash2, Copy, ChevronLeft, ChevronRight,
  ChevronUp, ChevronDown, ChevronsUpDown, Search, X,
  Phone, CheckCircle, XCircle, AlertCircle, User
} from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { toast } from 'sonner';

const initialFormData = {
  empresa_id: '', site_id: '', brand_id: '', asset_tag: '', serial_number: '',
  modelo: '', ip_address: '', mac_address: '', status_id: '', location_id: '',
  extension: '', assigned_user_id: '', supplier_id: '', purchase_date: '', comments: ''
};

const TelefonosIP = () => {
  const navigate = useNavigate();
  const { canRead, canCreate, canUpdate, canDelete } = usePermissions('micro', 'telefonos_ip');
  
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, sin_estado: 0, assigned: 0 });
  const [phones, setPhones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'asset_tag', dir: 'asc' });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], sites: [] });
  
  const { selectedIds, isAllSelected, isSomeSelected, isSelected, toggleRow, toggleAll, clearSelection } = useBulkSelection(phones);
  
  const [editModal, setEditModal] = useState({ open: false, phone: null, isCopy: false });
  const [deleteModal, setDeleteModal] = useState({ open: false, phone: null });
  const [formData, setFormData] = useState(initialFormData);
  const [formOptions, setFormOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [], ubicaciones: [], proveedores: [], usuarios: [], clientes: [] });
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(25);

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/telefonos-ip/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchPhones = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort_by: sortConfig.field,
        sort_dir: sortConfig.dir
      });
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.asset_tag_filter) params.append('asset_tag_filter', filters.asset_tag_filter);
      if (filters.modelo_filter) params.append('modelo_filter', filters.modelo_filter);
      if (filters.ip_filter) params.append('ip_filter', filters.ip_filter);
      if (filters.usuario_filter) params.append('usuario_filter', filters.usuario_filter);
      if (filters.legajo_filter) params.append('legajo_filter', filters.legajo_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);

      const response = await api.get(`/assets/telefonos-ip?${params}`);
      setPhones(response.data.items);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Error al cargar teléfonos');
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/telefonos-ip/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/telefonos-ip/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchPhones();
  }, [page, pageSize, sortConfig]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchPhones();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
    setActiveStatFilter('all');
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const handleStatFilter = (filterType) => {
    setActiveStatFilter(filterType);
    if (filterType === 'all') {
      setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', usuario_filter: '', legajo_filter: '', location_filter: '' });
    } else if (filterType === 'active') {
      setFilters(prev => ({ ...prev, status_id: '6' }));
    } else if (filterType === 'inactive') {
      setFilters(prev => ({ ...prev, status_id: '7' }));
    }
  };

  const openCreateModal = () => {
    setFormData(initialFormData);
    setEditModal({ open: true, phone: null, isCopy: false });
  };

  const openEditModal = (phone) => {
    setFormData({
      empresa_id: phone.empresa_id?.toString() || '',
      site_id: phone.site_id?.toString() || '',
      brand_id: phone.brand_id?.toString() || '',
      asset_tag: phone.asset_tag || '',
      serial_number: phone.serial_number || '',
      modelo: phone.modelo || '',
      ip_address: phone.ip_address || '',
      mac_address: phone.mac_address || '',
      status_id: phone.status_id?.toString() || '',
      location_id: phone.location_id?.toString() || '',
      extension: phone.extension || '',
      assigned_user_id: phone.assigned_user_id?.toString() || '',
      supplier_id: phone.supplier_id?.toString() || '',
      cliente_id: phone.cliente_id?.toString() || '',
      purchase_date: phone.purchase_date || '',
      comments: phone.comments || ''
    });
    setEditModal({ open: true, phone, isCopy: false });
  };

  const openCopyModal = (phone) => {
    setFormData({
      empresa_id: phone.empresa_id?.toString() || '',
      site_id: phone.site_id?.toString() || '',
      brand_id: phone.brand_id?.toString() || '',
      asset_tag: '',
      serial_number: '',
      modelo: phone.modelo || '',
      ip_address: '',
      mac_address: '',
      status_id: phone.status_id?.toString() || '',
      location_id: phone.location_id?.toString() || '',
      extension: '',
      assigned_user_id: '',
      supplier_id: phone.supplier_id?.toString() || '',
      purchase_date: phone.purchase_date || '',
      comments: phone.comments || ''
    });
    setEditModal({ open: true, phone: null, isCopy: true });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        empresa_id: formData.empresa_id ? parseInt(formData.empresa_id) : null,
        site_id: formData.site_id ? parseInt(formData.site_id) : null,
        brand_id: formData.brand_id ? parseInt(formData.brand_id) : null,
        status_id: formData.status_id ? parseInt(formData.status_id) : null,
        location_id: formData.location_id ? parseInt(formData.location_id) : null,
        assigned_user_id: formData.assigned_user_id ? parseInt(formData.assigned_user_id) : null,
        supplier_id: formData.supplier_id ? parseInt(formData.supplier_id) : null
      };

      if (editModal.phone && !editModal.isCopy) {
        await api.put(`/assets/telefonos-ip/${editModal.phone.id}`, payload);
        toast.success('Teléfono actualizado exitosamente');
      } else {
        await api.post('/assets/telefonos-ip/', payload);
        toast.success(editModal.isCopy ? 'Teléfono copiado exitosamente' : 'Teléfono creado exitosamente');
      }
      
      setEditModal({ open: false, phone: null, isCopy: false });
      fetchPhones();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/telefonos-ip/${deleteModal.phone.id}`);
      toast.success('Teléfono eliminado exitosamente');
      setDeleteModal({ open: false, phone: null });
      fetchPhones();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (statusName) => {
    if (!statusName) return <span className="badge badge-warning">Sin Estado</span>;
    const lower = statusName.toLowerCase();
    if (lower.includes('activo') || lower.includes('active')) {
      return <span className="badge badge-success">{statusName}</span>;
    }
    if (lower.includes('inactivo') || lower.includes('inactive') || lower.includes('baja')) {
      return <span className="badge badge-danger">{statusName}</span>;
    }
    return <span className="badge badge-default">{statusName}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Teléfonos IP</h1>
            <p className="text-muted-foreground">Gestión de teléfonos IP y VoIP</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/telefonos-ip"
              filters={{ search: filters.search }}
              filename="telefonos_ip"
            />
            {canCreate && (
              <Button onClick={openCreateModal} data-testid="new-phone-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nuevo Teléfono
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards - Horizontal Compacto */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="telefonos-stats">
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-primary/50 ${activeStatFilter === 'all' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatFilter('all')}
            data-testid="stats-total"
          >
            <Phone className="h-6 w-6 text-primary flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value">{stats.total}</div>
              <div className="stats-card-label">Total</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-green-500/50 ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatFilter('active')}
            data-testid="stats-activos"
          >
            <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-green-500">{stats.active}</div>
              <div className="stats-card-label">Activos</div>
            </div>
          </div>
          <div 
            className={`stats-card flex items-center gap-3 cursor-pointer transition-all hover:ring-2 hover:ring-red-500/50 ${activeStatFilter === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatFilter('inactive')}
            data-testid="stats-inactivos"
          >
            <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-red-500">{stats.inactive}</div>
              <div className="stats-card-label">Inactivos</div>
            </div>
          </div>
          <div 
            className="stats-card flex items-center gap-3"
            data-testid="stats-asignados"
          >
            <User className="h-6 w-6 text-blue-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-blue-500">{stats.assigned}</div>
              <div className="stats-card-label">Asignados</div>
            </div>
          </div>
        </div>

        {/* Global Search */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-10 py-3 px-4">
                      <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} onCheckedChange={toggleAll} />
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => handleSort('asset_tag')}>
                      <div className="flex items-center gap-1">ASSET TAG <SortIcon field="asset_tag" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('brand_name')}>
                      <div className="flex items-center gap-1">MARCA <SortIcon field="brand_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer" onClick={() => handleSort('modelo')}>
                      <div className="flex items-center gap-1">MODELO <SortIcon field="modelo" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('cliente_name')}>
                      <div className="flex items-center gap-1">CLIENTE <SortIcon field="cliente_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('ip_address')}>
                      <div className="flex items-center gap-1">IP <SortIcon field="ip_address" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('user_name')}>
                      <div className="flex items-center gap-1">USUARIO <SortIcon field="user_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('usuario_legajo')}>
                      <div className="flex items-center gap-1">LEGAJO <SortIcon field="usuario_legajo" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('status_name')}>
                      <div className="flex items-center gap-1">ESTADO <SortIcon field="status_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('site_name')}>
                      <div className="flex items-center gap-1">SITE <SortIcon field="site_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('location_name')}>
                      <div className="flex items-center gap-1">UBICACIÓN <SortIcon field="location_name" /></div>
                    </th>
                    <th className="w-28 text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground">ACCIONES</th>
                  </tr>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="w-10 py-2 px-4"></th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." value={filters.asset_tag_filter} onChange={(e) => handleFilterChange('asset_tag_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.brands.map(b => <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." value={filters.modelo_filter} onChange={(e) => handleFilterChange('modelo_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {formOptions.clientes?.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." value={filters.ip_filter} onChange={(e) => handleFilterChange('ip_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4">
                      <Input placeholder="Filtrar..." value={filters.usuario_filter} onChange={(e) => handleFilterChange('usuario_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Input placeholder="Filtrar..." value={filters.legajo_filter} onChange={(e) => handleFilterChange('legajo_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.statuses.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.sites.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Input placeholder="Filtrar..." value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} className="h-7 text-xs bg-background" />
                    </th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={11} className="text-center py-8 text-muted-foreground">Cargando...</td></tr>
                  ) : phones.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-8">
                        <Phone className="h-12 w-12 text-muted-foreground mx-auto mb-3" strokeWidth={1.5} />
                        <p className="text-muted-foreground">No se encontraron teléfonos</p>
                      </td>
                    </tr>
                  ) : (
                    phones.map((phone) => (
                      <tr key={phone.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${isSelected(phone.id) ? 'bg-primary/5' : ''}`} data-testid={`phone-row-${phone.id}`}>
                        <td className="w-10 py-3 px-4">
                          <Checkbox checked={isSelected(phone.id)} onCheckedChange={() => toggleRow(phone.id)} />
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{phone.asset_tag || '-'}</td>
                        <td className="py-3 px-4">{phone.brand_name || '-'}</td>
                        <td className="py-3 px-4">{phone.modelo || '-'}</td>
                        <td className="py-3 px-4">{phone.cliente_name || '-'}</td>
                        <td className="py-3 px-4 font-mono text-sm">{phone.ip_address || '-'}</td>
                        <td className="py-3 px-4">{phone.assigned_user_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{phone.usuario_legajo || '-'}</td>
                        <td className="py-3 px-4">{getStatusBadge(phone.status_name)}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{phone.site_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{phone.location_name || '-'}</td>
                        <td className="w-28 py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/micro/ip-phones/${phone.id}`)} title="Ver">
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-primary" onClick={() => openEditModal(phone)} title="Editar">
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-muted-foreground" onClick={() => openCopyModal(phone)} title="Copiar">
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" onClick={() => setDeleteModal({ open: true, phone })} title="Eliminar">
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} de {total}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <Select
                      value={pageSize.toString()}
                      onValueChange={(value) => { setPageSize(parseInt(value)); setPage(1); }}
                    >
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">Página {page} de {totalPages}</span>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={editModal.open} onOpenChange={(open) => setEditModal({ open, phone: null, isCopy: false })}>
          <DialogContent className="max-w-2xl" aria-describedby="phone-form-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editModal.isCopy ? <Copy className="h-5 w-5" strokeWidth={1.5} /> : editModal.phone ? <Edit className="h-5 w-5" strokeWidth={1.5} /> : <Plus className="h-5 w-5" strokeWidth={1.5} />}
                {editModal.isCopy ? 'Copiar Teléfono' : editModal.phone ? 'Editar Teléfono' : 'Nuevo Teléfono'}
              </DialogTitle>
            </DialogHeader>
            <p id="phone-form-desc" className="text-sm text-muted-foreground">Complete los datos del teléfono IP.</p>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input value={formData.asset_tag} onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))} placeholder="TEL-001" />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input value={formData.serial_number} onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={formData.brand_id} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.brands.map(b => <SelectItem key={b.id} value={b.id.toString()}>{b.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input value={formData.modelo} onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Extensión</Label>
                <Input value={formData.extension} onChange={(e) => setFormData(prev => ({ ...prev, extension: e.target.value }))} placeholder="1234" />
              </div>
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input value={formData.ip_address} onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))} placeholder="192.168.1.100" />
              </div>
              <div className="space-y-2">
                <Label>MAC Address</Label>
                <Input value={formData.mac_address} onChange={(e) => setFormData(prev => ({ ...prev, mac_address: e.target.value }))} placeholder="00:11:22:33:44:55" />
              </div>
              <div className="space-y-2">
                <Label>Usuario Asignado</Label>
                <Select value={formData.assigned_user_id} onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_user_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.usuarios.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Empresa *</Label>
                <Select value={formData.empresa_id} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas.map(e => <SelectItem key={e.id} value={e.id.toString()}>{e.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Site *</Label>
                <Select value={formData.site_id} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.sites.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ubicación</Label>
                <Select value={formData.location_id} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones.map(u => <SelectItem key={u.id} value={u.id.toString()}>{u.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={formData.status_id} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses.map(s => <SelectItem key={s.id} value={s.id.toString()}>{s.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={formData.supplier_id} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores.map(p => <SelectItem key={p.id} value={p.id.toString()}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={formData.cliente_id} onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {formOptions.clientes?.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input type="date" value={formData.purchase_date} onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))} />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Comentarios</Label>
                <Textarea value={formData.comments} onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))} rows={2} />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditModal({ open: false, phone: null, isCopy: false })}>Cancelar</Button>
              <Button onClick={handleSave} disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={deleteModal.open} onOpenChange={(open) => setDeleteModal({ open, phone: null })}>
          <DialogContent aria-describedby="delete-phone-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Teléfono
              </DialogTitle>
            </DialogHeader>
            <p id="delete-phone-desc" className="text-muted-foreground">
              ¿Eliminar el teléfono <strong>{deleteModal.phone?.asset_tag}</strong>?
              <br /><span className="text-sm">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModal({ open: false, phone: null })}>Cancelar</Button>
              <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchPhones(); fetchStats(); }}
          endpoint="/assets/telefonos-ip"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.statuses,
            ubicaciones: formOptions.ubicaciones
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id']}
        />
      </div>
    </Layout>
  );
};

export default TelefonosIP;
